import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import type { RootState } from 'store';

import { identifyLDUser } from 'common/featureFlags/launchDarkly';
import { buildAnonymousLDContext, buildUserLDContext } from 'common/featureFlags/launchDarklyContext';

/**
 * Identifies the user on login, re-identifies when an attribute changes (study/site selection, role
 * switch), and resets to anonymous on logout. The signature check suppresses redundant calls.
 */
export const useLaunchDarklyIdentify = (): void => {
    const activeUser = useSelector((state: RootState) => state.auth?.activeUser);
    const activeStudySite = useSelector((state: RootState) => state.auth?.activeStudySite);

    const identifiedSignatureRef = useRef<string>();

    useEffect(() => {
        if (!activeUser?.pk) {
            // Only on logout — on first load the client already bootstrapped anonymous.
            if (identifiedSignatureRef.current) {
                identifiedSignatureRef.current = undefined;
                identifyLDUser(buildAnonymousLDContext());
            }

            return;
        }

        const context = buildUserLDContext(activeUser, activeStudySite);
        const signature = JSON.stringify(context);

        if (identifiedSignatureRef.current === signature) {
            return;
        }

        identifiedSignatureRef.current = signature;
        identifyLDUser(context);
    }, [activeUser, activeStudySite]);
};
